<template>
    <div class="device-type-distribution-container">
        <div class="ranking-list">
            <div
                v-for="(item, index) in deviceTypeList"
                :key="index"
                class="ranking-item"
                :class="`rank-${index + 1}`"
            >
                <div class="rank-number">
                    <span class="rank-text">TOP.{{ index + 1 }}</span>
                </div>
                <div class="device-info">
                    <div class="device-name" :style="deviceNameStyle">{{ item.name }}</div>
                    <div class="progress-container">
                        <div class="progress-bar">
                            <div
                                class="progress-fill"
                                :style="{
                                    width: `${(item.count / maxCount) * 100}%`,
                                    background: item.color
                                }"
                            ></div>
                        </div>
                        <div class="device-count" :style="deviceCountStyle">{{ item.count }}</div>
                    </div>
                </div>
            </div>
        </div>
        <DashboardLoading
            :visible="initLoading && loading"
            text="加载设备类型分布数据"
            size="normal"
        />
    </div>
</template>

<script>
import { getDeviceTypeStats } from '@/api/dashboard'
import DashboardLoading from './DashboardLoading.vue'

export default {
  name: 'DeviceTypeDistribution',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        titleFontSize: 16,
        legendFontSize: 11,
        axisLabelFontSize: 10,
        axisNameFontSize: 12,
        tooltipFontSize: 14,
        dataLabelFontSize: 11,
        smallFontSize: 8,
        largeFontSize: 18
      })
    }
  },
  data () {
    return {
      loading: false,
      deviceTypeList: [],
      initLoading: true
    }
  },
  computed: {
    // 设备名称样式
    deviceNameStyle () {
      return {
        fontSize: `${this.fontSizes.smallFontSize + 2}px`
      }
    },
    // 设备数量样式
    deviceCountStyle () {
      return {
        fontSize: `${this.fontSizes.dataLabelFontSize}px`
      }
    },
    // 计算最大数量，用于进度条比例
    maxCount () {
      return Math.max(...this.deviceTypeList.map(item => item.count))
    }
  },
  mounted () {
    this.fetchDeviceTypeData()
  },
  methods: {
    async fetchDeviceTypeData () {
      try {
        this.loading = true
        const response = await getDeviceTypeStats()

        if (response.data && response.data.status && response.data.data) {
          // 处理API返回的数据，添加颜色属性
          this.deviceTypeList = response.data.data.map(item => ({
            name: item.name,
            count: item.count,
            color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
          }))
        } else {
          console.error('获取设备类型分布数据失败:', (response.data && response.data.msg) || '未知错误')
          // 使用默认数据作为备用
          this.setDefaultData()
        }
      } catch (error) {
        console.error('获取设备类型分布数据异常:', error)
        // 使用默认数据作为备用
        this.setDefaultData()
      } finally {
        this.loading = false
        this.initLoading = false
      }
    },

    setDefaultData () {
      // 备用数据
      this.deviceTypeList = [
        {
          name: '通用基础版',
          count: 790,
          color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
        },
        {
          name: '服务窗口版',
          count: 201,
          color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
        },
        {
          name: '智能运维',
          count: 50,
          color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
        },
        {
          name: '云知印mini',
          count: 6,
          color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
        },
        {
          name: '其它',
          count: 0,
          color: 'linear-gradient(90deg, #00D4FF 0%, #0099CC 100%)'
        }
      ]
    }
  }
}
</script>

<style lang="less" scoped>
.device-type-distribution-container {
    width: 100%;
    flex: 1;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 0;
}

.ranking-list {
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 400px;
    min-width: 280px;
    flex: 1;
}

.ranking-item {
    display: flex;
    align-items: center;
    padding: 0 10px;
    border-radius: var(--border-radius, 6px);
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    min-height: 35px;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover {
        transform: translateX(5px);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.2);

        &::before {
            opacity: 1;
        }
    }

    // TOP1 特殊样式
    &.rank-1 {
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 153, 204, 0.08) 100%);
        border: 1px solid rgba(0, 212, 255, 0.3);

        .rank-text {
            color: #00D4FF;
            text-shadow: 0 0 8px rgba(0, 212, 255, 0.6);
        }

        &:hover {
            box-shadow: 0 6px 20px rgba(0, 212, 255, 0.3);
        }
    }

    // TOP2 样式
    &.rank-2 {
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.12) 0%, rgba(0, 153, 204, 0.06) 100%);
        border: 1px solid rgba(0, 212, 255, 0.25);

        .rank-text {
            color: #33DDFF;
        }
    }

    // TOP3 样式
    &.rank-3 {
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 153, 204, 0.05) 100%);
        border: 1px solid rgba(0, 212, 255, 0.2);

        .rank-text {
            color: #66E6FF;
        }
    }
}

.rank-number {
    width: 55px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;
    margin-right: 10px;

    .rank-text {
        color: rgba(255, 255, 255, 0.8);
        font-weight: 600;
        font-size: 10px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
    }
}

.device-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.device-name {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    letter-spacing: 0.3px;
    line-height: 1.2;
}

.progress-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.progress-bar {
    flex: 1;
    height: 3px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 1.5px;
    overflow: hidden;
    position: relative;

    .progress-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 1s ease-out;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 20px;
            height: 100%;
            background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 100%);
            animation: shimmer 2s ease-in-out infinite;
        }
    }
}

.device-count {
    color: #ffffff;
    font-weight: 600;
    font-family: 'Arial', 'Microsoft YaHei', sans-serif;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
    min-width: 35px;
    text-align: right;
}

// 响应式设计 - 超大屏幕 (1920px+)
@media (min-width: 1920px) {
    .device-type-distribution-container {
        // padding: 10px 10px 15px 10px;
    }

    .ranking-list {
        gap: 16px;
        max-width: 500px;
    }

    .ranking-item {
        padding: 0 16px;
        min-height: 55px;
    }

    .rank-number {
        width: 70px;
        margin-right: 16px;

        .rank-text {
            font-size: 15px;
            font-weight: 800;
        }
    }

    .device-info {
        gap: 8px;
    }

    .progress-bar {
        height: 5px;
    }
}

// 大屏幕 (1440px - 1919px)
@media (min-width: 1440px) and (max-width: 1919px) {
    .device-type-distribution-container {
        // padding: 10px 10px 15px 10px;
    }

    .ranking-list {
        gap: 14px;
        max-width: 450px;
    }

    .ranking-item {
        padding: 0 14px;
        min-height: 50px;
    }

    .rank-number {
        width: 65px;
        margin-right: 14px;

        .rank-text {
            font-size: 12px;
        }
    }

    .device-info {
        gap: 7px;
    }
}

// 中等屏幕 (1200px - 1439px)
@media (min-width: 1200px) and (max-width: 1439px) {
    .ranking-list {
        max-width: 420px;
    }

    .ranking-item {
        padding: 0 13px;
        min-height: 48px;
    }

    .rank-number {
        width: 62px;
        margin-right: 13px;
    }
}

// 小屏幕桌面 (1024px - 1199px)
@media (min-width: 1024px) and (max-width: 1199px) {
    .ranking-list {
        max-width: 380px;
    }

    .ranking-item {
        padding: 0 12px;
        min-height: 45px;
    }

    .rank-number {
        width: 58px;
        margin-right: 12px;

        .rank-text {
            font-size: 10px;
        }
    }
}

// 小屏幕平板 (768px - 1023px)
@media (min-width: 768px) and (max-width: 1023px) {
    .device-type-distribution-container {
        // padding: 10px 10px 15px 10px;
    }

    .ranking-list {
        gap: 10px;
        max-width: 360px;
    }

    .ranking-item {
        padding: 0 10px;
        min-height: 42px;
    }

    .rank-number {
        width: 55px;
        margin-right: 10px;

        .rank-text {
            font-size: 9px;
        }
    }

    .device-info {
        gap: 5px;
    }

    .progress-bar {
        height: 3px;
    }
}

// 手机屏幕 (最大768px)
@media (max-width: 767px) {
    .device-type-distribution-container {
        // padding: 10px;
    }

    .ranking-list {
        gap: 8px;
        max-width: 100%;
    }

    .ranking-item {
        padding: 0 8px;
        min-height: 38px;
    }

    .rank-number {
        width: 50px;
        margin-right: 8px;

        .rank-text {
            font-size: 8px;
        }
    }

    .device-info {
        gap: 4px;
    }

    .progress-container {
        gap: 8px;
    }

    .device-count {
        min-width: 30px;
    }
}

// 动画效果
@keyframes shimmer {
    0% {
        transform: translateX(-20px);
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        transform: translateX(20px);
        opacity: 0;
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.ranking-item {
    animation: fadeInUp 0.6s ease-out;

    &.rank-1 { animation-delay: 0.1s; }
    &.rank-2 { animation-delay: 0.2s; }
    &.rank-3 { animation-delay: 0.3s; }
    &.rank-4 { animation-delay: 0.4s; }
    &.rank-5 { animation-delay: 0.5s; }
}

// 进度条动画
.progress-fill {
    animation: progressLoad 1.5s ease-out 0.8s both;
}

@keyframes progressLoad {
    from {
        width: 0% !important;
    }
}
</style>
